// @owners { team: patients }
import {
  type OrderBundlingOrigin,
  OrderBundlingOriginMap,
} from '@alto/scriptdash/alto/patient_app/scheduling/order_bundling/types/v1/order_bundling_origin';
import { type OrderBundlingEndpointOrderBundlingOptionsRequest } from '@alto/scriptdash/alto/patient_app/scheduling/v1/order_bundling_endpoint';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getCartPrescriptionResourceIds } from '~shared/features/cart/helpers/getCartPrescriptionResourceIds';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getEditShipmentID } from '~shared/features/checkout/selectors/getCheckout';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { isMail } from '~shared/helpers/order';
import { staleTimes } from '~shared/queries/constants';
import { queries } from '~shared/queries/query-keys';
import { useSelectorShared } from '~shared/store';

type OrderBundlingOptions = {
  origin: OrderBundlingOrigin;
  address_id: OrderBundlingEndpointOrderBundlingOptionsRequest['address_id'];
};

export const getOrderBundlingCheckoutOrigin = ({
  isEditingOrder,
}: {
  isEditingOrder: boolean;
}): OrderBundlingOrigin => {
  if (isEditingOrder) {
    return OrderBundlingOriginMap.EDIT_ORDER;
  } else {
    return OrderBundlingOriginMap.CHECKOUT;
  }
};

export const useOrderBundlingOptions = ({ address_id, origin }: OrderBundlingOptions) => {
  const { cartItems } = useCartData();
  const cartPrescriptionResourceIds = useMemo(() => getCartPrescriptionResourceIds(cartItems), [cartItems]);
  const cart_prescription_ids = cartPrescriptionResourceIds.join(',');
  const shipmentID = useSelectorShared(getEditShipmentID);
  const shipment_id = origin === 'edit_order' ? shipmentID : null;
  const orderIndex = useSelectorShared(getOrderIndex);
  const { deliveryMethods } = useCartDeliveryMethods();
  const deliveryMethod = deliveryMethods?.[orderIndex];
  const isMailOrder = isMail(deliveryMethod?.mode);
  const enabled = cart_prescription_ids.length > 0 && !!origin && !!address_id && !isMailOrder;

  const { data, isError, isLoading, isSuccess } = useQuery({
    ...queries.orderBundling.options({
      address_id,
      cart_prescription_ids,
      origin,
      edit_order_shipment_id: shipment_id,
    }),
    staleTime: staleTimes.oneMinute,
    enabled,
  });
  const orderBundlingOptions = data?.data?.bundled_options;
  const cartShipment = orderBundlingOptions?.new_shipment;
  const earliestUpcomingShipment = orderBundlingOptions?.original_shipments[0];
  const earliestUpcomingShipmentPrescriptions = earliestUpcomingShipment?.prescriptions;

  const bundledPrescriptionIDs: number[] = useMemo(() => {
    if (earliestUpcomingShipmentPrescriptions && cartPrescriptionResourceIds.length > 0) {
      return [...cartPrescriptionResourceIds, ...earliestUpcomingShipmentPrescriptions.map((rx) => rx.id)];
    } else {
      return [];
    }
  }, [cartPrescriptionResourceIds, earliestUpcomingShipmentPrescriptions]);

  const canCombine = isSuccess && !!cartShipment?.prescriptions?.length && !!earliestUpcomingShipment?.id;

  return {
    canCombine,
    isError,
    isLoading,
    isSuccess,
    orderBundlingOptions,
    earliestUpcomingShipment,
    cartShipment,
    bundledPrescriptionIDs,
  };
};
